import { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Tabs, Tab } from '@mui/material';
import NotesBox from './NotesBox';
import Demographics from './Demographics';
import AvailableServices from './AvailableServices';
import Assessments from './Assessments';
import VonageDeviceSettings from './VonageDeviceSettings';

export function Panel({
  allAppointmentNotes = [],
  clientData = {},
  appointment,
  appointmentNoteID = null,
  assessments,
  showDeviceSettings = false,
  deviceSettings = null,
  liveCaptions = null,
  locale = 'en',
}) {
  const [activeTabKey, setActiveTabKey] = useState(null);
  const tabsRef = useRef({});

  const initializeTabs = (providerType, showSettings) => {
    const notes = {
      notes: {
        name: <FormattedMessage defaultMessage="Notes" />,
      },
    };

    const assessmentsTab = {
      assessments: {
        name: <FormattedMessage defaultMessage="Assessments" />,
      },
    };

    const demographics = {
      demographics: {
        name: <FormattedMessage defaultMessage="Client Info" />,
      },
    };

    const workplaceBenefits = {
      workplace_benefits: {
        name: <FormattedMessage defaultMessage="Available Services" />,
      },
    };

    const settings = showSettings
      ? {
          settings: {
            name: <FormattedMessage defaultMessage="Settings" />,
          },
        }
      : {};

    switch (providerType) {
      case 'couples':
        return {
          ...notes,
          ...assessmentsTab,
          ...settings,
        };
      case 'generic':
        return {
          ...notes,
          ...demographics,
          ...workplaceBenefits,
          ...settings,
        };
      default:
        return {
          ...notes,
          ...demographics,
          ...assessmentsTab,
          ...workplaceBenefits,
          ...settings,
        };
    }
  };

  useEffect(() => {
    const tabs = initializeTabs(
      appointment.provider_type,
      showDeviceSettings,
    );
    tabsRef.current = tabs;
    setActiveTabKey(Object.keys(tabs)[0]);
    // Match class componentDidMount: run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeTab = newKey => {
    setActiveTabKey(newKey);
  };

  const renderCurrentTab = () => {
    switch (activeTabKey) {
      case 'notes':
        return (
          appointmentNoteID && (
            <NotesBox
              appointmentNoteID={appointmentNoteID}
              allAppointmentNotes={allAppointmentNotes}
            />
          )
        );
      case 'demographics':
        return <Demographics clientData={clientData} />;
      case 'workplace_benefits':
        return <AvailableServices clientData={clientData} />;
      case 'assessments':
        return <Assessments assessments={assessments} />;
      case 'settings':
        return deviceSettings ? (
          <VonageDeviceSettings
            controller={deviceSettings.controller}
            hasVideoSource={deviceSettings.hasVideoSource}
            liveCaptions={liveCaptions}
            locale={locale}
          />
        ) : (
          <div />
        );
      default:
        return <div />;
    }
  };

  return appointment ? (
    <div
      style={{
        padding: '20px 20px',
        boxShadow: '0 0 20px 1px rgba(146, 180, 255, 0.4)',
        backgroundColor: 'white',
        borderRadius: 8,
        minHeight: showDeviceSettings ? 670 : 600,
      }}
    >
      <Tabs
        value={activeTabKey}
        onChange={(event, newValue) => onChangeTab(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        slotProps={{ indicator: { style: { background: 'black' } } }}
      >
        {Object.entries(tabsRef.current).map(([key, tab]) => (
          <Tab key={key} value={key} label={tab.name} />
        ))}
      </Tabs>
      <div style={{ padding: '30px 10px 10px 10px' }}>
        {renderCurrentTab()}
      </div>
    </div>
  ) : (
    appointmentNoteID && (
      <NotesBox
        appointmentNoteID={appointmentNoteID}
        allAppointmentNotes={allAppointmentNotes}
      />
    )
  );
}

Panel.propTypes = {
  allAppointmentNotes: PropTypes.array,
  clientData: PropTypes.object,
  appointment: PropTypes.object.isRequired,
  appointmentNoteID: PropTypes.number,
  assessments: PropTypes.array.isRequired,
  showDeviceSettings: PropTypes.bool,
  deviceSettings: PropTypes.shape({
    controller: PropTypes.object.isRequired,
    hasVideoSource: PropTypes.bool.isRequired,
  }),
  liveCaptions: PropTypes.shape({
    available: PropTypes.bool.isRequired,
    isEnabled: PropTypes.bool.isRequired,
    isStarting: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
  }),
  locale: PropTypes.string,
};

function mapStateToProps(state) {
  const appointment = _.get(state, 'session.appointment');
  const appointmentNote = _.find(appointment?.appointment_notes, {
    user_id: state.user.id,
  });

  return {
    appointment,
    appointmentNoteID: appointmentNote ? appointmentNote.id : null,
  };
}

export default connect(mapStateToProps, {})(Panel);
